import React from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { dashboardHref } from 'helpers/dashboardSession';

const DASHBOARD_HREF = dashboardHref('nav');
const DASHBOARD_LABEL = 'Dashboard';
const DASHBOARD_ARIA_LABEL = 'View Dashboard';

const isDashboardPath = (slug: string): boolean => {
  const normalizedSlug = slug.replace(/^\/+/, '');
  return normalizedSlug === 'dashboard' || normalizedSlug.startsWith('dashboard/');
};

interface DashboardNavItemProps {
  slug: string;
}

export const DashboardNavItem: React.FC<DashboardNavItemProps> = ({ slug }) => {
  const { authentication } = useAuth();
  const isDashboardActive = isDashboardPath(slug);

  if (authentication.state === 'authenticating') {
    return <DashboardNavSkeleton />;
  }

  if (authentication.state !== 'authenticated') {
    return null;
  }

  return (
    <Box role="listitem" className="relative block pl-3">
      <Box
        href={DASHBOARD_HREF}
        aria-label={DASHBOARD_ARIA_LABEL}
        aria-current={isDashboardActive ? 'page' : undefined}
        analyticsName="cosmos-mega-nav-dashboard"
        className="inline-flex h-11 items-center justify-center rounded-xl border-0 bg-white/90 px-6 font-medium text-od-content-primary no-underline shadow-[0_0_0_1px_rgb(88_64_50_/_0.16),inset_0_0_0_1px_rgb(255_255_255_/_0.3),inset_0_-4px_0_0_rgb(88_64_50_/_0.1)] transition hover:bg-white/90 hover:shadow-[0_0_0_1px_rgb(88_64_50_/_0.3),inset_0_0_0_1px_rgb(255_255_255_/_0.3),inset_0_-4px_0_0_rgb(88_64_50_/_0.05)] focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-[#81a2f2] active:bg-[linear-gradient(rgb(88_64_50_/_0.1),rgb(88_64_50_/_0.1))] active:shadow-[0_0_0_1px_rgb(88_64_50_/_0.3),inset_0_0_0_1px_rgb(255_255_255_/_0.3)]"
      >
        <Text as="span" className="text-[16px] font-medium tracking-[-0.8px] text-inherit">
          {DASHBOARD_LABEL}
        </Text>
      </Box>
    </Box>
  );
};

const DashboardNavSkeleton: React.FC = () => {
  return (
    <Box role="listitem" aria-hidden className="relative block pl-3">
      <Box
        data-dashboard-nav-skeleton
        className="inline-flex h-11 items-center justify-center rounded-xl border-0 bg-white/90 px-6 shadow-[0_0_0_1px_rgb(88_64_50_/_0.16),inset_0_0_0_1px_rgb(255_255_255_/_0.3),inset_0_-4px_0_0_rgb(88_64_50_/_0.1)]"
      >
        <Box className="skeleton h-4 w-[84px] rounded-md bg-od-bg-tertiary" />
      </Box>
    </Box>
  );
};
